import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { TOOLS_CATEGORIES } from "../constants/catalog";
import { Input } from "../components/ui/input";
import { ArrowUpRight, Search, ArrowRight } from "lucide-react";

export default function ToolsLanding() {
    const [counts, setCounts] = useState({});
    const [cats, setCats] = useState(TOOLS_CATEGORIES);
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/categories?group=tools").then((r) => { if (r.data?.length) setCats(r.data); }).catch(() => {});
        api.get("/catalog/categories").then((r) => {
            const map = {};
            (r.data.tools || []).forEach((c) => { map[c.slug] = c.count; });
            setCounts(map);
        });
    }, []);

    const visible = cats.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()));

    return (
        <div data-testid="tools-landing">
            {/* HERO */}
            <section className="relative h-[62vh] min-h-[460px] overflow-hidden">
                <img src="https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=2000&q=80" alt="Professional tint tools" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-16">
                    <div className="max-w-3xl animate-fade-up">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-12 h-px bg-gold" />
                            <span className="text-[11px] tracking-[0.4em] uppercase text-gold">Tools & Accessories</span>
                        </div>
                        <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">Professional Tint Tools &amp; Accessories</h1>
                        <p className="mt-6 text-[#33302C] max-w-xl text-base md:text-lg font-light leading-relaxed">
                            Shop installation tools, squeegees, blades, prep supplies, meters, and accessories trusted by tint professionals.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <a href="#categories" data-testid="hero-shop-tools" className="px-8 py-4 bg-gold text-white font-semibold uppercase tracking-[0.2em] text-xs hover:bg-gold-hover transition-colors inline-flex items-center gap-2">Shop Tools <ArrowUpRight size={16} /></a>
                            <Link to="/contact" data-testid="hero-dealer-pricing" className="px-8 py-4 border border-[#8C5A3C]/40 text-[#1A1A1A] uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors">Request Dealer Pricing</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CATEGORY GRID */}
            <section id="categories" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
                    <div>
                        <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Shop by Category</div>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight">Find the right tool for the job</h2>
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B645C]" />
                        <Input data-testid="tools-category-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search categories…" className="pl-9 bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {visible.map((c, i) => (
                        <Link
                            key={c.slug}
                            to={`/tools/${c.slug}`}
                            data-testid={`tools-category-${c.slug}`}
                            className="group bg-charcoal-800 border border-[#E2D4C1] hover:border-gold/50 transition-all duration-500 flex flex-col"
                        >
                            <div className="aspect-[16/10] overflow-hidden bg-[#E2D4C1] relative">
                                <img src={c.image} alt={c.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase px-2 py-1 bg-[#F7F3EB]/90 border border-[#E2D4C1] text-[#33302C]">
                                    {counts[c.slug] ?? 0} item{(counts[c.slug] ?? 0) !== 1 ? "s" : ""}
                                </span>
                            </div>
                            <div className="p-5 flex-1 flex flex-col">
                                <h3 className="font-display text-lg leading-tight mb-2">{c.name}</h3>
                                <p className="text-[#6B645C] text-xs leading-relaxed flex-1 mb-4">{c.description || c.desc}</p>
                                <span className="text-[11px] tracking-[0.2em] uppercase text-gold inline-flex items-center gap-1 group-hover:gap-2 transition-all">View Products <ArrowRight size={13} /></span>
                            </div>
                        </Link>
                    ))}
                    {visible.length === 0 && <div className="col-span-full text-center text-[#6B645C] py-16">No categories match “{q}”.</div>}
                </div>
            </section>
        </div>
    );
}
